import { Tooltip, type TooltipProps } from "@mui/material";
import cx from "classnames";
import { useGlobalLocalContext } from "src/storage/localStorage.ts";
import WrapIf from "@components/utils/WrapIf/WrapIf";
import { DATETIME_FORMAT } from "@utils/time";
import { formatTimeTransformerDisplay } from "./TimeTransformer.utils";

interface TimeTransformerProps {
  // timestamp in ISO 8601 format
  ts?: string;
  withoutTooltip?: boolean;
  // mode: "yyyy-MM-dd HH:mm:ss" | "humanReadable" (Apr 15, 2023 10:30 AM)
  mode?: string | "humanReadable";
  format?: Intl.DateTimeFormatOptions;
  multiline?: boolean;
  // Render the timestamp as-is, ignoring the tz selector. Use for logical PIT
  // values (a job's point-in-time label).
  ignoreTimezone?: boolean;
  slotProps?: {
    tooltip?: Partial<TooltipProps>;
  };
  className?: string;
}

const TimeTransformer = ({
  ts,
  withoutTooltip,
  multiline,
  ignoreTimezone,
  className,
  slotProps,
  ...props
}: TimeTransformerProps) => {
  props.mode ||= DATETIME_FORMAT;

  const storeTz = useGlobalLocalContext((state) => state.tz);
  const tz = ignoreTimezone ? 0 : storeTz;

  if (!ts) return null;

  const display = formatTimeTransformerDisplay({
    ts,
    tz,
    mode: props.mode,
    format: props.format,
    multiline,
  });
  if (!display) return null;

  const { formattedTime, relativeDate } = display;

  return (
    <WrapIf
      condition={!withoutTooltip}
      With={Tooltip}
      arrow
      disableInteractive
      {...slotProps?.tooltip}
      title={
        <>
          {slotProps?.tooltip?.title}
          <div
            className={cx({ "opacity-70 font-thin": !!slotProps?.tooltip?.title })}
          >
            {relativeDate}
          </div>
        </>
      }
    >
      <div
        className={cx("inline-flex flex-wrap gap-1 leading-3", className)}
        style={{ textDecoration: "inherit" }} // allows this component to be wrapped with a link and inherit it's underline
      >
        {multiline ? (
          <>
            <span>{formattedTime[0]}</span>
            <wbr />
            <span>{formattedTime[1]}</span>
          </>
        ) : (
          formattedTime
        )}
      </div>
    </WrapIf>
  );
};

export default TimeTransformer;
